/**
 * Evidence genealogy, as deterministic identity arithmetic.
 *
 * An item is a report, page, paper, dataset, registration, trial, cohort,
 * experimental run, review, recorded outcome, or corpus passage. A family is the
 * underlying evidentiary event those items describe. Keeping both levels is the
 * point: three reports may be three inspectable records and one independence unit.
 *
 * No model participates. Every union is justified by a named rule and the exact
 * inputs that fired it. Human overrides are constraints over those unions. They
 * are processed newest first, `separate` wins an equal-time tie, and every human
 * decision outranks every mechanical edge. That ordering makes an override a
 * durable instruction rather than an annotation a later inference can ignore.
 */

import { canonicalUrl, doiOf, registrableHost } from './url'
import {
  SYNDICATION_HAMMING_MAX,
  fnv1a64,
  foldText,
  hamming,
  simHash as signatureFor,
  textHash as exactHashFor,
} from './similarity'

export const EVIDENCE_ORIGINS = [
  'web',
  'scholarly',
  'corpus',
  'registry',
  'dataset',
  'trial',
  'cohort',
  'experimental-run',
  'review',
  'outcome',
] as const

export type EvidenceOrigin = (typeof EVIDENCE_ORIGINS)[number]

export const GENEALOGY_RULES = [
  'human-same',
  'human-separate',
  'same-doi',
  'shared-trial',
  'shared-dataset',
  'overlapping-dataset',
  'shared-registry',
  'overlapping-registry',
  'shared-run',
  'overlapping-run',
  'shared-cohort',
  'overlapping-cohort',
  'inherited-primary',
  'common-origin',
  'exact-text',
  'near-text',
] as const

export type GenealogyRule = (typeof GENEALOGY_RULES)[number]
type MechanicalRule = Exclude<GenealogyRule, 'human-same' | 'human-separate'>

/**
 * The order used only where a human separation leaves more than one mechanically
 * valid maximal partition. Structured identifiers go before content similarity;
 * exact content goes before its locality-sensitive approximation. The values are
 * exported so a local verifier never has to infer this policy from array order.
 */
export const GENEALOGY_RULE_PRECEDENCE: Readonly<Record<MechanicalRule, number>> = {
  'same-doi': 10,
  'shared-trial': 20,
  'shared-dataset': 30,
  'overlapping-dataset': 35,
  'shared-registry': 40,
  'overlapping-registry': 45,
  'shared-run': 50,
  'overlapping-run': 55,
  'shared-cohort': 60,
  'overlapping-cohort': 65,
  'inherited-primary': 70,
  'common-origin': 80,
  'exact-text': 90,
  'near-text': 100,
}

/** One independently inspectable record. Arrays are explicit identities, not prose. */
export interface EvidenceRecord {
  id: string
  origin: EvidenceOrigin
  label: string
  url?: string
  /** A bare DOI, `doi:…`, or a doi.org URL. Invalid values merge nothing. */
  doi?: string
  /** Source body or abstract. Labels and titles are never hashed as evidence. */
  text?: string
  /** Stored signatures may stand in where the source text is no longer retained. */
  textHash?: string
  simHash?: string
  trialIds?: readonly string[]
  datasetIds?: readonly string[]
  registryIds?: readonly string[]
  runIds?: readonly string[]
  cohortIds?: readonly string[]
  /**
   * Stable analyst- or extractor-supplied overlap groups. Sharing one means the
   * records reuse at least part of the named population or material, even when
   * their primary identifiers differ. The binary merge is deliberately
   * conservative: partially dependent evidence cannot earn two independence units.
   */
  datasetOverlapGroups?: readonly string[]
  registryOverlapGroups?: readonly string[]
  runOverlapGroups?: readonly string[]
  cohortOverlapGroups?: readonly string[]
  /** Item ids or stable external identities for primary evidence this item inherits. */
  primaryReferences?: readonly string[]
  /** Stable identities for a source, artefact, or event several records descend from. */
  commonOrigins?: readonly string[]
}

/** The normalized, serializable record used by every rule. */
export interface EvidenceItem {
  id: string
  origin: EvidenceOrigin
  label: string
  url?: string
  doi?: string
  textHash?: string
  simHash?: string
  trialIds: string[]
  datasetIds: string[]
  registryIds: string[]
  runIds: string[]
  cohortIds: string[]
  datasetOverlapGroups: string[]
  registryOverlapGroups: string[]
  runOverlapGroups: string[]
  cohortOverlapGroups: string[]
  primaryReferences: string[]
  commonOrigins: string[]
}

/** Persist this record and pass it back on the next build. The reason is required. */
export interface GenealogyOverride {
  id: string
  leftId: string
  rightId: string
  decision: 'same' | 'separate'
  reason: string
  at: number
}

export type RuleInput = string | number | boolean | string[]

export type MergeOutcome =
  'merged' | 'already-family' | 'blocked' | 'separated' | 'superseded' | 'conflict'

/** One candidate edge or human separation, including edges that did not land. */
export interface MergeDecision {
  id: string
  kind: 'merge' | 'separate'
  rule: GenealogyRule
  leftId: string
  rightId: string
  source: 'mechanical' | 'human'
  inputs: Record<string, RuleInput>
  /** Human is always above mechanical. Smaller mechanical ranks run first. */
  precedence:
    | { tier: 'human'; at: number; equalTime: 'separate-first' }
    | { tier: 'mechanical'; rank: number }
  outcome: MergeOutcome
  /** Higher-precedence human decision ids that prevented this one from landing. */
  blockedBy?: string[]
}

export interface EvidenceFamily {
  id: string
  itemIds: string[]
  origins: EvidenceOrigin[]
  /** Successful merge rules represented inside this family. */
  rules: GenealogyRule[]
  mergeDecisionIds: string[]
  independenceUnitId: string
}

/** The countable projection. Its items remain separately inspectable in `items`. */
export interface IndependenceUnit {
  id: string
  familyId: string
  itemIds: string[]
  itemCount: number
  origins: EvidenceOrigin[]
  basis: GenealogyRule[]
}

export type GenealogyDiagnosticCode =
  | 'invalid-item'
  | 'duplicate-item-id'
  | 'invalid-doi'
  | 'doi-conflict'
  | 'invalid-signature'
  | 'signature-conflict'
  | 'invalid-override'
  | 'duplicate-override-id'
  | 'unknown-override-item'
  | 'self-override'
  | 'superseded-override'
  | 'override-conflict'
  | 'merge-blocked'

export interface GenealogyDiagnostic {
  id: string
  code: GenealogyDiagnosticCode
  severity: 'info' | 'warning'
  message: string
  itemIds: string[]
  decisionIds: string[]
}

export interface EvidenceGenealogy {
  items: EvidenceItem[]
  families: EvidenceFamily[]
  mergeDecisions: MergeDecision[]
  independenceUnits: IndependenceUnit[]
  independenceCount: number
  diagnostics: GenealogyDiagnostic[]
}

interface PendingDiagnostic extends Omit<GenealogyDiagnostic, 'id'> {}

interface CandidateEdge {
  rule: MechanicalRule
  leftId: string
  rightId: string
  inputs: Record<string, RuleInput>
}

interface NormalizedOverride extends GenealogyOverride {
  leftId: string
  rightId: string
}

interface Separation {
  leftId: string
  rightId: string
  decisionId: string
}

const ORIGIN_RANK = new Map<EvidenceOrigin, number>(
  EVIDENCE_ORIGINS.map((origin, index) => [origin, index]),
)
const ORIGIN_SET = new Set<string>(EVIDENCE_ORIGINS)
const HASH = /^[0-9a-f]{16}$/i
const DOI = /^10\.\d{4,9}\/[^\s"'<>]+$/i

const compareText = (a: string, b: string): number => (a < b ? -1 : a > b ? 1 : 0)

const sorted = (values: Iterable<string>): string[] => [...values].sort(compareText)

const unique = (values: Iterable<string>): string[] => sorted(new Set(values))

const compact = (value: string): string => value.trim().replace(/\s+/g, ' ')

const normalizedKey = (value: string): string => foldText(value)

const normalizedKeys = (values: readonly string[] | undefined): string[] =>
  unique((values ?? []).map(normalizedKey).filter(Boolean))

const preservedKeys = (values: readonly string[] | undefined): string[] =>
  unique((values ?? []).map(compact).filter(Boolean))

const pair = (a: string, b: string): [string, string] => (compareText(a, b) <= 0 ? [a, b] : [b, a])

const pairKey = (a: string, b: string): string => pair(a, b).join('\u0000')

const stableInputs = (inputs: Record<string, RuleInput>): string =>
  Object.keys(inputs)
    .sort(compareText)
    .map((key) => {
      const value = inputs[key]
      return `${key}=${Array.isArray(value) ? value.join('\u0001') : String(value)}`
    })
    .join('\u0002')

const stableItem = (item: EvidenceItem): string =>
  [
    item.id,
    item.origin,
    item.label,
    item.url ?? '',
    item.doi ?? '',
    item.textHash ?? '',
    item.simHash ?? '',
    item.trialIds.join('\u0001'),
    item.datasetIds.join('\u0001'),
    item.registryIds.join('\u0001'),
    item.runIds.join('\u0001'),
    item.cohortIds.join('\u0001'),
    item.datasetOverlapGroups.join('\u0001'),
    item.registryOverlapGroups.join('\u0001'),
    item.runOverlapGroups.join('\u0001'),
    item.cohortOverlapGroups.join('\u0001'),
    item.primaryReferences.join('\u0001'),
    item.commonOrigins.join('\u0001'),
  ].join('\u0002')

const stableOverride = (override: NormalizedOverride): string =>
  [
    override.id,
    override.leftId,
    override.rightId,
    override.decision,
    override.reason,
    String(override.at),
  ].join('\u0002')

function pendingDiagnostic(
  code: GenealogyDiagnosticCode,
  severity: GenealogyDiagnostic['severity'],
  message: string,
  itemIds: Iterable<string> = [],
  decisionIds: Iterable<string> = [],
): PendingDiagnostic {
  return {
    code,
    severity,
    message,
    itemIds: unique(itemIds),
    decisionIds: unique(decisionIds),
  }
}

function finalizeDiagnostics(rows: PendingDiagnostic[]): GenealogyDiagnostic[] {
  const out = new Map<string, GenealogyDiagnostic>()
  for (const row of rows) {
    const body = [
      row.code,
      row.severity,
      row.message,
      row.itemIds.join('\u0001'),
      row.decisionIds.join('\u0001'),
    ].join('\u0002')
    const id = `diagnostic_${fnv1a64(body)}`
    out.set(id, { id, ...row })
  }
  return [...out.values()].sort(
    (a, b) =>
      compareText(a.code, b.code) ||
      compareText(a.itemIds.join('\u0001'), b.itemIds.join('\u0001')) ||
      compareText(a.id, b.id),
  )
}

function bareDoi(value: string | undefined): string | undefined {
  if (!value) return undefined
  let candidate = compact(value).toLowerCase()
  candidate = candidate.replace(/^doi:\s*/, '')
  candidate = candidate.replace(/^https?:\/\/(?:dx\.)?doi\.org\//, '')
  try {
    candidate = decodeURIComponent(candidate)
  } catch {
    return undefined
  }
  candidate = candidate.replace(/\/+$/, '')
  return DOI.test(candidate) ? candidate : undefined
}

function storedSignature(
  raw: string | undefined,
  itemId: string,
  field: 'textHash' | 'simHash',
  diagnostics: PendingDiagnostic[],
): string | undefined {
  if (raw === undefined) return undefined
  const value = compact(raw).toLowerCase()
  if (HASH.test(value)) return value
  diagnostics.push(
    pendingDiagnostic(
      'invalid-signature',
      'warning',
      `${itemId} carries an invalid ${field}; that value was ignored.`,
      [itemId],
    ),
  )
  return undefined
}

interface TextSignatures {
  readonly textHash?: string
  readonly simHash?: string
}

/**
 * How many characters of record text `signaturesOf` keeps signatures for. The
 * largest exploration measured, the 12 MB evidence fixture, carries 10.6 million.
 */
export const SIGNED_TEXT_BUDGET = 16_000_000

const signedTexts = new Map<string, TextSignatures>()
let signedChars = 0

/**
 * A record text's two signatures, taken over its folded form, remembered by the
 * exact text they were taken over.
 *
 * The same texts are signed again and again. Every read of a document rebuilds its
 * records — the codec signs each one on the way in, and every save reads its own
 * bytes back through that codec before it commits — and every surface that
 * projects a genealogy (a claim's staleness, the coach's one-study count) signs
 * them again for each new session object, which is every edit. On the 12 MB
 * evidence fixture that was three full passes per edit and seven at boot. Keyed by
 * the record OBJECT, a cache caught the surfaces and missed every read, because a
 * read builds new objects (measured per edit: 232 of 348 signings by object, 348 of
 * 348 by text). So the key is the text.
 *
 * A hit is exactly the computation it replaces. Both signatures are pure functions
 * of the text, so nothing is remembered that the text does not determine and the
 * collapse stays reproducible by hand from the stored record.
 *
 * Bounded in characters rather than entries, because what a key costs is its
 * length. Least recently read goes first; past the budget a text is signed again
 * when next read — slower, never different.
 */
function signaturesOf(text: string): TextSignatures {
  const held = signedTexts.get(text)
  if (held) {
    signedTexts.delete(text)
    signedTexts.set(text, held)
    return held
  }
  const body = foldText(text)
  const fresh: TextSignatures = body
    ? { textHash: exactHashFor(body), simHash: signatureFor(body) }
    : {}
  if (text.length <= SIGNED_TEXT_BUDGET) {
    signedTexts.set(text, fresh)
    signedChars += text.length
    for (const key of signedTexts.keys()) {
      if (signedChars <= SIGNED_TEXT_BUDGET) break
      signedTexts.delete(key)
      signedChars -= key.length
    }
  }
  return fresh
}

function normalizeRecord(
  record: EvidenceRecord,
  diagnostics: PendingDiagnostic[],
): EvidenceItem | null {
  const id = compact(record.id)
  if (!id || !ORIGIN_SET.has(record.origin)) {
    diagnostics.push(
      pendingDiagnostic(
        'invalid-item',
        'warning',
        'An evidence item needs a non-empty id and a recognized origin.',
        id ? [id] : [],
      ),
    )
    return null
  }

  const url = record.url ? canonicalUrl(record.url) : undefined
  const explicitRaw = record.doi ? compact(record.doi) : undefined
  const explicitDoi = bareDoi(explicitRaw)
  if (explicitRaw && !explicitDoi) {
    diagnostics.push(
      pendingDiagnostic(
        'invalid-doi',
        'warning',
        `${id} carries a DOI-shaped value that cannot identify a work; it was ignored.`,
        [id],
      ),
    )
  }
  const urlDoi = record.url ? doiOf(record.url) : undefined
  const doi = explicitDoi && urlDoi && explicitDoi !== urlDoi ? undefined : (explicitDoi ?? urlDoi)
  if (explicitDoi && urlDoi && explicitDoi !== urlDoi) {
    diagnostics.push(
      pendingDiagnostic(
        'doi-conflict',
        'warning',
        `${id} names different works in its DOI field and URL; neither identity was used.`,
        [id],
      ),
    )
  }

  const suppliedExact = storedSignature(record.textHash, id, 'textHash', diagnostics)
  const suppliedNear = storedSignature(record.simHash, id, 'simHash', diagnostics)
  const signed = record.text === undefined ? undefined : signaturesOf(record.text)
  const computedExact = signed?.textHash
  const computedNear = signed?.simHash

  if (signed && suppliedExact && suppliedExact !== computedExact) {
    diagnostics.push(
      pendingDiagnostic(
        'signature-conflict',
        'warning',
        `${id}'s stored textHash disagrees with its supplied text; the text-derived value won.`,
        [id],
      ),
    )
  }
  if (signed && suppliedNear && suppliedNear !== computedNear) {
    diagnostics.push(
      pendingDiagnostic(
        'signature-conflict',
        'warning',
        `${id}'s stored simHash disagrees with its supplied text; the text-derived value won.`,
        [id],
      ),
    )
  }

  return {
    id,
    origin: record.origin,
    label: compact(record.label) || id,
    ...(url ? { url } : {}),
    ...(doi ? { doi } : {}),
    ...((computedExact ?? (signed === undefined ? suppliedExact : undefined))
      ? { textHash: computedExact ?? suppliedExact }
      : {}),
    ...((computedNear ?? (signed === undefined ? suppliedNear : undefined))
      ? { simHash: computedNear ?? suppliedNear }
      : {}),
    trialIds: normalizedKeys(record.trialIds),
    datasetIds: normalizedKeys(record.datasetIds),
    registryIds: normalizedKeys(record.registryIds),
    runIds: normalizedKeys(record.runIds),
    cohortIds: normalizedKeys(record.cohortIds),
    datasetOverlapGroups: normalizedKeys(record.datasetOverlapGroups),
    registryOverlapGroups: normalizedKeys(record.registryOverlapGroups),
    runOverlapGroups: normalizedKeys(record.runOverlapGroups),
    cohortOverlapGroups: normalizedKeys(record.cohortOverlapGroups),
    primaryReferences: preservedKeys(record.primaryReferences),
    commonOrigins: normalizedKeys(record.commonOrigins),
  }
}

function normalizeItems(
  records: readonly EvidenceRecord[],
  diagnostics: PendingDiagnostic[],
): EvidenceItem[] {
  const rows = records
    .map((record) => normalizeRecord(record, diagnostics))
    .filter((item): item is EvidenceItem => item !== null)
    .sort((a, b) => compareText(a.id, b.id) || compareText(stableItem(a), stableItem(b)))

  const items: EvidenceItem[] = []
  for (let i = 0; i < rows.length;) {
    const first = rows[i]!
    let end = i + 1
    while (end < rows.length && rows[end]!.id === first.id) end++
    items.push(first)
    if (end - i > 1) {
      diagnostics.push(
        pendingDiagnostic(
          'duplicate-item-id',
          'warning',
          `${first.id} appeared ${end - i} times; one deterministic normalized record was retained.`,
          [first.id],
        ),
      )
    }
    i = end
  }
  return items
}

function normalizeOverrides(
  raw: readonly GenealogyOverride[],
  itemIds: Set<string>,
  diagnostics: PendingDiagnostic[],
): NormalizedOverride[] {
  const rows: NormalizedOverride[] = []
  for (const value of raw) {
    const id = compact(value.id)
    const reason = compact(value.reason)
    const left = compact(value.leftId)
    const right = compact(value.rightId)
    const at = Number(value.at)
    if (
      !id ||
      !reason ||
      !Number.isFinite(at) ||
      at < 0 ||
      (value.decision !== 'same' && value.decision !== 'separate')
    ) {
      diagnostics.push(
        pendingDiagnostic(
          'invalid-override',
          'warning',
          'A genealogy override needs an id, a reason, a non-negative timestamp, and a decision.',
          [left, right].filter(Boolean),
        ),
      )
      continue
    }
    if (!itemIds.has(left) || !itemIds.has(right)) {
      diagnostics.push(
        pendingDiagnostic(
          'unknown-override-item',
          'warning',
          `${id} names an evidence item that is not present; the override was ignored.`,
          [left, right],
        ),
      )
      continue
    }
    if (left === right) {
      diagnostics.push(
        pendingDiagnostic(
          'self-override',
          'warning',
          `${id} compares an item with itself; the override was ignored.`,
          [left],
        ),
      )
      continue
    }
    const [leftId, rightId] = pair(left, right)
    rows.push({
      id,
      leftId,
      rightId,
      decision: value.decision,
      reason,
      at,
    })
  }

  rows.sort((a, b) => compareText(a.id, b.id) || compareText(stableOverride(a), stableOverride(b)))
  const deduped: NormalizedOverride[] = []
  for (let i = 0; i < rows.length;) {
    const first = rows[i]!
    let end = i + 1
    while (end < rows.length && rows[end]!.id === first.id) end++
    deduped.push(first)
    if (end - i > 1) {
      diagnostics.push(
        pendingDiagnostic(
          'duplicate-override-id',
          'warning',
          `${first.id} appeared ${end - i} times; one deterministic record was retained.`,
          [first.leftId, first.rightId],
        ),
      )
    }
    i = end
  }
  return deduped
}

function overrideOrder(a: NormalizedOverride, b: NormalizedOverride): number {
  if (a.at !== b.at) return b.at - a.at
  if (a.decision !== b.decision) return a.decision === 'separate' ? -1 : 1
  return compareText(b.id, a.id)
}

function decisionId(
  source: MergeDecision['source'],
  rule: GenealogyRule,
  leftId: string,
  rightId: string,
  inputs: Record<string, RuleInput>,
): string {
  return `decision_${fnv1a64([source, rule, leftId, rightId, stableInputs(inputs)].join('\u0002'))}`
}

function overrideDecision(
  override: NormalizedOverride,
  outcome: MergeOutcome,
  blockedBy?: string[],
): MergeDecision {
  const rule = override.decision === 'same' ? 'human-same' : 'human-separate'
  const inputs: Record<string, RuleInput> = {
    overrideId: override.id,
    reason: override.reason,
    at: override.at,
  }
  return {
    id: decisionId('human', rule, override.leftId, override.rightId, inputs),
    kind: override.decision === 'same' ? 'merge' : 'separate',
    rule,
    leftId: override.leftId,
    rightId: override.rightId,
    source: 'human',
    inputs,
    precedence: { tier: 'human', at: override.at, equalTime: 'separate-first' },
    outcome,
    ...(blockedBy?.length ? { blockedBy: unique(blockedBy) } : {}),
  }
}

function candidateId(edge: CandidateEdge): string {
  return [edge.rule, edge.leftId, edge.rightId, stableInputs(edge.inputs)].join('\u0002')
}

function candidateEdges(items: EvidenceItem[]): CandidateEdge[] {
  const found = new Map<string, CandidateEdge>()
  const byId = new Set(items.map((item) => item.id))

  const add = (
    rule: MechanicalRule,
    a: string,
    b: string,
    inputs: Record<string, RuleInput>,
  ): void => {
    if (a === b) return
    const [leftId, rightId] = pair(a, b)
    const edge = { rule, leftId, rightId, inputs }
    found.set(candidateId(edge), edge)
  }

  const group = (
    rule: MechanicalRule,
    rows: Array<{ itemId: string; key: string }>,
    inputName: string,
  ): void => {
    const groups = new Map<string, Set<string>>()
    for (const row of rows) {
      const held = groups.get(row.key)
      if (held) held.add(row.itemId)
      else groups.set(row.key, new Set([row.itemId]))
    }
    for (const key of sorted(groups.keys())) {
      const ids = sorted(groups.get(key) ?? [])
      for (let i = 0; i < ids.length; i++) {
        for (let j = i + 1; j < ids.length; j++) {
          add(rule, ids[i]!, ids[j]!, { [inputName]: key })
        }
      }
    }
  }

  group(
    'same-doi',
    items.flatMap((item) => (item.doi ? [{ itemId: item.id, key: item.doi }] : [])),
    'doi',
  )
  group(
    'exact-text',
    items.flatMap((item) => (item.textHash ? [{ itemId: item.id, key: item.textHash }] : [])),
    'textHash',
  )

  const shared: Array<{
    rule: MechanicalRule
    inputName: string
    values: (item: EvidenceItem) => string[]
  }> = [
    { rule: 'shared-trial', inputName: 'trialId', values: (item) => item.trialIds },
    { rule: 'shared-dataset', inputName: 'datasetId', values: (item) => item.datasetIds },
    { rule: 'shared-registry', inputName: 'registryId', values: (item) => item.registryIds },
    { rule: 'shared-run', inputName: 'runId', values: (item) => item.runIds },
    { rule: 'shared-cohort', inputName: 'cohortId', values: (item) => item.cohortIds },
    {
      rule: 'overlapping-dataset',
      inputName: 'datasetOverlapGroup',
      values: (item) => item.datasetOverlapGroups,
    },
    {
      rule: 'overlapping-registry',
      inputName: 'registryOverlapGroup',
      values: (item) => item.registryOverlapGroups,
    },
    {
      rule: 'overlapping-run',
      inputName: 'runOverlapGroup',
      values: (item) => item.runOverlapGroups,
    },
    {
      rule: 'overlapping-cohort',
      inputName: 'cohortOverlapGroup',
      values: (item) => item.cohortOverlapGroups,
    },
    { rule: 'common-origin', inputName: 'commonOrigin', values: (item) => item.commonOrigins },
  ]
  for (const spec of shared) {
    group(
      spec.rule,
      items.flatMap((item) => spec.values(item).map((key) => ({ itemId: item.id, key }))),
      spec.inputName,
    )
  }

  const primaryRows: Array<{ itemId: string; key: string }> = []
  for (const item of items) {
    for (const reference of item.primaryReferences) {
      const key = byId.has(reference) ? `item:${reference}` : `origin:${normalizedKey(reference)}`
      primaryRows.push({ itemId: item.id, key })
      // A local primary record belongs to the family its derivative names, even
      // though a primary item does not need to list itself as its own reference.
      if (byId.has(reference)) primaryRows.push({ itemId: reference, key })
    }
  }
  group('inherited-primary', primaryRows, 'primaryReference')

  for (let i = 0; i < items.length; i++) {
    const left = items[i]!
    if (!left.simHash) continue
    for (let j = i + 1; j < items.length; j++) {
      const right = items[j]!
      if (!right.simHash) continue
      const distance = hamming(left.simHash, right.simHash)
      if (distance > SYNDICATION_HAMMING_MAX) continue
      add('near-text', left.id, right.id, {
        leftSimHash: left.simHash,
        rightSimHash: right.simHash,
        hamming: distance,
        maximum: SYNDICATION_HAMMING_MAX,
      })
    }
  }

  return [...found.values()].sort(
    (a, b) =>
      GENEALOGY_RULE_PRECEDENCE[a.rule] - GENEALOGY_RULE_PRECEDENCE[b.rule] ||
      compareText(a.leftId, b.leftId) ||
      compareText(a.rightId, b.rightId) ||
      compareText(stableInputs(a.inputs), stableInputs(b.inputs)),
  )
}

class DisjointFamilies {
  private readonly parent = new Map<string, string>()

  constructor(ids: readonly string[]) {
    for (const id of ids) this.parent.set(id, id)
  }

  find(id: string): string {
    const held = this.parent.get(id)
    if (!held || held === id) return held ?? id
    const root = this.find(held)
    this.parent.set(id, root)
    return root
  }

  union(a: string, b: string): boolean {
    const left = this.find(a)
    const right = this.find(b)
    if (left === right) return false
    // The lexically first root always wins. Component identity therefore cannot
    // depend on which equivalent edge happened to be visited first.
    const [root, child] = pair(left, right)
    this.parent.set(child, root)
    return true
  }
}

function separationConflicts(
  families: DisjointFamilies,
  leftId: string,
  rightId: string,
  separations: readonly Separation[],
): string[] {
  const leftRoot = families.find(leftId)
  const rightRoot = families.find(rightId)
  if (leftRoot === rightRoot) return []
  const conflicts: string[] = []
  for (const separation of separations) {
    const a = families.find(separation.leftId)
    const b = families.find(separation.rightId)
    if ((a === leftRoot && b === rightRoot) || (a === rightRoot && b === leftRoot)) {
      conflicts.push(separation.decisionId)
    }
  }
  return unique(conflicts)
}

function addHumanPath(
  graph: Map<string, Array<{ id: string; decisionId: string }>>,
  leftId: string,
  rightId: string,
  decisionIdValue: string,
): void {
  const left = graph.get(leftId) ?? []
  left.push({ id: rightId, decisionId: decisionIdValue })
  graph.set(leftId, left)
  const right = graph.get(rightId) ?? []
  right.push({ id: leftId, decisionId: decisionIdValue })
  graph.set(rightId, right)
}

/** The higher-priority human same path that made an older separation impossible. */
function humanPath(
  graph: Map<string, Array<{ id: string; decisionId: string }>>,
  from: string,
  to: string,
): string[] {
  const queue = [from]
  const seen = new Set([from])
  const previous = new Map<string, { id: string; decisionId: string }>()
  while (queue.length) {
    const at = queue.shift()!
    if (at === to) break
    const next = [...(graph.get(at) ?? [])].sort(
      (a, b) => compareText(a.id, b.id) || compareText(a.decisionId, b.decisionId),
    )
    for (const edge of next) {
      if (seen.has(edge.id)) continue
      seen.add(edge.id)
      previous.set(edge.id, { id: at, decisionId: edge.decisionId })
      queue.push(edge.id)
    }
  }
  if (!seen.has(to)) return []
  const decisions: string[] = []
  let cursor = to
  while (cursor !== from) {
    const step = previous.get(cursor)
    if (!step) break
    decisions.push(step.decisionId)
    cursor = step.id
  }
  return unique(decisions)
}

function decisionOrder(a: MergeDecision, b: MergeDecision): number {
  if (a.source !== b.source) return a.source === 'human' ? -1 : 1
  if (a.precedence.tier === 'human' && b.precedence.tier === 'human') {
    if (a.precedence.at !== b.precedence.at) return b.precedence.at - a.precedence.at
    if (a.kind !== b.kind) return a.kind === 'separate' ? -1 : 1
  }
  if (a.precedence.tier === 'mechanical' && b.precedence.tier === 'mechanical') {
    if (a.precedence.rank !== b.precedence.rank) return a.precedence.rank - b.precedence.rank
  }
  return (
    compareText(a.leftId, b.leftId) ||
    compareText(a.rightId, b.rightId) ||
    compareText(a.rule, b.rule) ||
    compareText(a.id, b.id)
  )
}

/**
 * Build the complete genealogy. Pure over its inputs: same records and overrides,
 * in any arrival order, produce byte-identical JSON.
 */
export function buildEvidenceGenealogy(
  records: readonly EvidenceRecord[],
  overrides: readonly GenealogyOverride[] = [],
): EvidenceGenealogy {
  const pending: PendingDiagnostic[] = []
  const items = normalizeItems(records, pending)
  const itemIds = new Set(items.map((item) => item.id))
  const normalizedOverrides = normalizeOverrides(overrides, itemIds, pending)
  const families = new DisjointFamilies(items.map((item) => item.id))
  const decisions: MergeDecision[] = []
  const separations: Separation[] = []
  const humanSameGraph = new Map<string, Array<{ id: string; decisionId: string }>>()

  const byPair = new Map<string, NormalizedOverride[]>()
  for (const override of normalizedOverrides) {
    const key = pairKey(override.leftId, override.rightId)
    const list = byPair.get(key)
    if (list) list.push(override)
    else byPair.set(key, [override])
  }

  const effective: NormalizedOverride[] = []
  for (const key of sorted(byPair.keys())) {
    const rows = byPair.get(key)!.sort(overrideOrder)
    effective.push(rows[0]!)
    for (const old of rows.slice(1)) {
      const decision = overrideDecision(old, 'superseded')
      decisions.push(decision)
      pending.push(
        pendingDiagnostic(
          'superseded-override',
          'info',
          `${old.id} was superseded by a newer decision on the same pair.`,
          [old.leftId, old.rightId],
          [decision.id],
        ),
      )
    }
  }

  effective.sort(overrideOrder)
  for (const override of effective) {
    if (override.decision === 'separate') {
      if (families.find(override.leftId) === families.find(override.rightId)) {
        const draft = overrideDecision(override, 'conflict')
        const blockedBy = humanPath(humanSameGraph, override.leftId, override.rightId)
        const decision = { ...draft, ...(blockedBy.length ? { blockedBy } : {}) }
        decisions.push(decision)
        pending.push(
          pendingDiagnostic(
            'override-conflict',
            'warning',
            `${override.id} could not separate a family already joined by newer human decisions.`,
            [override.leftId, override.rightId],
            [decision.id, ...blockedBy],
          ),
        )
        continue
      }
      const decision = overrideDecision(override, 'separated')
      decisions.push(decision)
      separations.push({
        leftId: override.leftId,
        rightId: override.rightId,
        decisionId: decision.id,
      })
      continue
    }

    const blockedBy = separationConflicts(families, override.leftId, override.rightId, separations)
    if (blockedBy.length) {
      const decision = overrideDecision(override, 'blocked', blockedBy)
      decisions.push(decision)
      pending.push(
        pendingDiagnostic(
          'override-conflict',
          'warning',
          `${override.id} was blocked by a newer human separation.`,
          [override.leftId, override.rightId],
          [decision.id, ...blockedBy],
        ),
      )
      continue
    }
    const merged = families.union(override.leftId, override.rightId)
    const decision = overrideDecision(override, merged ? 'merged' : 'already-family')
    decisions.push(decision)
    addHumanPath(humanSameGraph, override.leftId, override.rightId, decision.id)
  }

  for (const edge of candidateEdges(items)) {
    const blockedBy = separationConflicts(families, edge.leftId, edge.rightId, separations)
    const outcome: MergeOutcome = blockedBy.length
      ? 'blocked'
      : families.union(edge.leftId, edge.rightId)
        ? 'merged'
        : 'already-family'
    const decision: MergeDecision = {
      id: decisionId('mechanical', edge.rule, edge.leftId, edge.rightId, edge.inputs),
      kind: 'merge',
      rule: edge.rule,
      leftId: edge.leftId,
      rightId: edge.rightId,
      source: 'mechanical',
      inputs: edge.inputs,
      precedence: { tier: 'mechanical', rank: GENEALOGY_RULE_PRECEDENCE[edge.rule] },
      outcome,
      ...(blockedBy.length ? { blockedBy } : {}),
    }
    decisions.push(decision)
    if (blockedBy.length) {
      pending.push(
        pendingDiagnostic(
          'merge-blocked',
          'info',
          `${edge.rule} matched ${edge.leftId} and ${edge.rightId}, but a human separation kept them apart.`,
          [edge.leftId, edge.rightId],
          [decision.id, ...blockedBy],
        ),
      )
    }
  }

  decisions.sort(decisionOrder)
  const components = new Map<string, string[]>()
  for (const item of items) {
    const root = families.find(item.id)
    const list = components.get(root)
    if (list) list.push(item.id)
    else components.set(root, [item.id])
  }

  const byItem = new Map(items.map((item) => [item.id, item]))
  const successful = decisions.filter(
    (decision) =>
      decision.kind === 'merge' &&
      (decision.outcome === 'merged' || decision.outcome === 'already-family'),
  )
  const familyRows: EvidenceFamily[] = [...components.values()]
    .map((memberIds) => {
      const itemIdsSorted = sorted(memberIds)
      const memberSet = new Set(itemIdsSorted)
      const mergeDecisions = successful.filter(
        (decision) => memberSet.has(decision.leftId) && memberSet.has(decision.rightId),
      )
      const hash = fnv1a64(itemIdsSorted.join('\u0000'))
      const familyId = `family_${hash}`
      const independenceUnitId = `unit_${hash}`
      const origins = [...new Set(itemIdsSorted.map((id) => byItem.get(id)!.origin))].sort(
        (a, b) => ORIGIN_RANK.get(a)! - ORIGIN_RANK.get(b)!,
      )
      return {
        id: familyId,
        itemIds: itemIdsSorted,
        origins,
        rules: unique(mergeDecisions.map((decision) => decision.rule)) as GenealogyRule[],
        mergeDecisionIds: sorted(mergeDecisions.map((decision) => decision.id)),
        independenceUnitId,
      }
    })
    .sort((a, b) => compareText(a.itemIds[0] ?? '', b.itemIds[0] ?? ''))

  const independenceUnits: IndependenceUnit[] = familyRows.map((family) => ({
    id: family.independenceUnitId,
    familyId: family.id,
    itemIds: [...family.itemIds],
    itemCount: family.itemIds.length,
    origins: [...family.origins],
    basis: [...family.rules],
  }))

  return {
    items,
    families: familyRows,
    mergeDecisions: decisions,
    independenceUnits,
    independenceCount: independenceUnits.length,
    diagnostics: finalizeDiagnostics(pending),
  }
}

/* ── read helpers ─────────────────────────────────────────────────────────
   For the surfaces outside the Lab that print what a built genealogy already
   decided: the node card's "5 reports · 1 independent" and the coach's grouping
   cue (`onestudy` in `cues.ts`). They read a genealogy and never build one, and
   they add no rule to it — so a card and the Lab's Evidence tab cannot count one
   claim's reports two ways, and a grouping a person accepts from the card lands
   as the same human override the Lab's form writes, in the same ledger. */

/** Which family each item sits in, once per genealogy. */
const familyIndexes = new WeakMap<EvidenceGenealogy, Map<string, string>>()

function familyIndex(genealogy: EvidenceGenealogy): Map<string, string> {
  const held = familyIndexes.get(genealogy)
  if (held) return held
  const index = new Map(
    genealogy.families.flatMap((family) => family.itemIds.map((id) => [id, family.id] as const)),
  )
  familyIndexes.set(genealogy, index)
  return index
}

/** How many reports a set of records is, and how many independence units. */
export interface IndependenceTally {
  /** Distinct records the genealogy holds among those asked about. */
  reports: number
  /** Distinct families those records fall in — the Lab's independence units. */
  independent: number
}

/**
 * The Lab's count, over a subset of its items.
 *
 * A family is counted however many of the asked records sit in it, and however
 * many records of OTHER claims sit in it too: independence is a property of the
 * whole genealogy, which is why this reads a built one rather than rebuilding
 * over the subset — two of this claim's reports joined only through a third
 * claim's record are still one unit. An id the genealogy dropped (an invalid
 * record) is not a report here either.
 */
export function independenceTally(
  genealogy: EvidenceGenealogy,
  recordIds: Iterable<string>,
): IndependenceTally {
  const familyOf = familyIndex(genealogy)
  const reports = new Set<string>()
  const units = new Set<string>()
  for (const id of recordIds) {
    const family = familyOf.get(id)
    if (!family) continue
    reports.add(id)
    units.add(family)
  }
  return { reports: reports.size, independent: units.size }
}

/**
 * A reason to think two records in DIFFERENT families may describe one study —
 * from inputs the genealogy already holds, below the bar at which it merges.
 *
 * Each is deliberately not a merge rule, and stays that way: the rules above
 * merge on identity, and these are resemblance. `near-text` is a page-text
 * signature past `SYNDICATION_HAMMING_MAX` — not a copy of one artefact, but
 * near enough to share much of its phrasing, which is what two write-ups of one
 * press release look like. `same-title` is the title the search API returned (a
 * label, which no rule here hashes as evidence) matching word for word, which is
 * what one paper on a publisher page and an index page looks like when neither
 * URL carries its DOI. Both are read only across two SITES. A person can accept
 * one; nothing here acts on one.
 */
export type KinSignal =
  { rule: 'near-text'; hamming: number } | { rule: 'same-title'; title: string }

export interface KinEdge {
  leftId: string
  rightId: string
  signal: KinSignal
}

/** The two thresholds a caller names — `cues.ts` owns them, so the sentence
 *  that prints them and the predicate that reads them are one edit. */
export interface KinThresholds {
  /** The widest signature distance read as shared phrasing, in bits of 64. */
  hammingMax: number
  /** The fewest words a title needs before a match says anything. */
  titleMinWords: number
}

/** A group of records the signals join across families. */
export interface KinCluster {
  /** The asked-about records it holds, sorted. */
  recordIds: string[]
  /** The families they sit in now: two or more, or it is not a cluster. */
  familyIds: string[]
  /**
   * One edge per join, `familyIds.length - 1` of them: the signal that brought
   * each family in. What a grouping records as the reason for each override.
   */
  joins: KinEdge[]
}

/** A title folded for comparison: case, punctuation and spacing are not the work. */
export function titleKey(label: string): string {
  return foldText(label.replace(/[^\p{L}\p{N}]+/gu, ' '))
}

/**
 * The records among `recordIds` that the signals above join across families,
 * largest group first.
 *
 * A human separation is honoured exactly as the merge pass honours it: two
 * groups a person kept apart are never offered as one, whichever records the
 * separation named. Joins are taken strongest first — the closest text, then a
 * title — so the reason recorded for each is the best one the pair has.
 */
export function kinClusters(
  genealogy: EvidenceGenealogy,
  recordIds: Iterable<string>,
  thresholds: KinThresholds,
): KinCluster[] {
  const familyOf = familyIndex(genealogy)
  const byId = new Map(genealogy.items.map((item) => [item.id, item]))
  const ids = unique([...recordIds].filter((id) => familyOf.has(id)))

  const edges: KinEdge[] = []
  for (let i = 0; i < ids.length; i++) {
    const left = byId.get(ids[i]!)!
    for (let j = i + 1; j < ids.length; j++) {
      const right = byId.get(ids[j]!)!
      if (familyOf.get(left.id) === familyOf.get(right.id)) continue
      // One site's two pages share its navigation, its footer and its house
      // phrasing, which is resemblance of a template rather than of a study; a
      // record with no address (a document of your own, an artefact) has no site
      // to share.
      if (left.url && right.url && registrableHost(left.url) === registrableHost(right.url))
        continue
      if (left.simHash && right.simHash) {
        const distance = hamming(left.simHash, right.simHash)
        // At or under the syndication bar the merge pass would already have
        // joined them, so a pair still apart there was kept apart by a person.
        if (distance > SYNDICATION_HAMMING_MAX && distance <= thresholds.hammingMax) {
          edges.push({
            leftId: left.id,
            rightId: right.id,
            signal: { rule: 'near-text', hamming: distance },
          })
          continue
        }
      }
      const title = titleKey(left.label)
      if (
        title &&
        title === titleKey(right.label) &&
        title.split(' ').length >= thresholds.titleMinWords
      ) {
        edges.push({
          leftId: left.id,
          rightId: right.id,
          signal: { rule: 'same-title', title: left.label },
        })
      }
    }
  }
  const strength = (edge: KinEdge): number =>
    edge.signal.rule === 'near-text' ? edge.signal.hamming : 65
  edges.sort(
    (a, b) =>
      strength(a) - strength(b) ||
      compareText(a.leftId, b.leftId) ||
      compareText(a.rightId, b.rightId),
  )

  const apart: Array<[string, string]> = genealogy.mergeDecisions
    .filter((row) => row.kind === 'separate' && row.outcome === 'separated')
    .flatMap((row) => {
      const a = familyOf.get(row.leftId)
      const b = familyOf.get(row.rightId)
      return a && b ? [[a, b] as [string, string]] : []
    })
  const groups = new DisjointFamilies(unique(ids.map((id) => familyOf.get(id)!)))
  const joins = new Map<string, KinEdge[]>()
  for (const edge of edges) {
    const a = groups.find(familyOf.get(edge.leftId)!)
    const b = groups.find(familyOf.get(edge.rightId)!)
    if (a === b) continue
    const kept = apart.some(([x, y]) => {
      const rx = groups.find(x)
      const ry = groups.find(y)
      return (rx === a && ry === b) || (rx === b && ry === a)
    })
    if (kept) continue
    const carried = [...(joins.get(a) ?? []), ...(joins.get(b) ?? []), edge]
    groups.union(a, b)
    joins.delete(a)
    joins.delete(b)
    joins.set(groups.find(a), carried)
  }

  const clusters = new Map<string, { recordIds: string[]; familyIds: Set<string> }>()
  for (const id of ids) {
    const family = familyOf.get(id)!
    const root = groups.find(family)
    const held = clusters.get(root) ?? { recordIds: [], familyIds: new Set<string>() }
    held.recordIds.push(id)
    held.familyIds.add(family)
    clusters.set(root, held)
  }
  return [...clusters.entries()]
    .filter(([, held]) => held.familyIds.size > 1)
    .map(([root, held]) => ({
      recordIds: sorted(held.recordIds),
      familyIds: sorted(held.familyIds),
      joins: joins.get(root) ?? [],
    }))
    .sort(
      (a, b) =>
        b.recordIds.length - a.recordIds.length ||
        b.familyIds.length - a.familyIds.length ||
        compareText(a.recordIds[0]!, b.recordIds[0]!),
    )
}
